import React, { useState, useRef, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Button } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { ResponseInfoType } from '../types';
import { ensureSessionId, fetchSSE, getSessionId, refreshSessionId } from '../api';
import { API_BASE_URL } from '../config/apiBase';
import SessionExpiredModal from '../components/modal/SessionExpiredModal';
import {
  isSessionExpiredResponse,
  subscribeSessionExpired,
  triggerSessionExpired,
} from '../utils/sessionExpired';
import {
  isPermitShortcutCard,
  openPermitPage,
  PERMIT_PREDEFINED_QUESTION_INDEX,
} from '../config/permitUrl';
import { isLifeInconvenienceAskCard } from '../config/lifeInconvenienceCard';
import { linkify } from '../utils/linkify';
import { isHoverMessageSecondaryLine } from '../utils/hoverMessageLines';
import { publicAsset } from '../utils/publicAsset';
import { scheduleIdleTask } from '../utils/scheduleIdleTask';

import useIsMobile from '../useIsMobile';

import StatusBar from '../components/StatusBar';
import ChatWindow from '../components/ChatWindow';
import ChatInput from '../components/ChatInput';
import Sidebar from '../components/Sidebar';
import NoticeBanner from '../components/NoticeBanner';
import LanguageSelectorComponent from '../components/LanguageSelector';
// 언어 관련 import
import { useLanguage } from '../contexts/LanguageContext';
import { Language, LANGUAGES } from '../types/language';
import { useDarkMode } from '../contexts/DarkModeContext';

// 아이콘 import
import { ReactComponent as ChevronIcon } from '../assets/icons/chevron.svg';
import { ReactComponent as MenuIcon } from '../assets/icons/menu.svg';
import { ReactComponent as CloseIcon } from '../assets/icons/close.svg';
import { ReactComponent as QuestionIcon } from '../assets/icons/question.svg';
import { GlobeIcon } from '../components/icon/icon';

// 다크모드 토글 import
import DarkModeToggle from '../components/DarkModeToggle';
import { getRelatedSiteUrl, isRelatedSiteLink, isRelatedSiteMenuLabel } from '../constants/relatedSites';
import { isNoticeMenuLabel } from '../constants/noticeMenu';
import { usePublicNotices } from '../hooks/usePublicNotices';


// 언어별 가이드 텍스트
const guideTexts = {
  ko: {
    title: '챗봇 이용 가이드',
    whatIs: 'AI충녕 챗봇은?',
    whatIsDesc: '세종시 관련 정보를 인공지능이 학습하여 답변해주는 서비스입니다.',
    howToUse: '이용 방법',
    howToUseItems: [
      '궁금한 점을 입력창에 자유롭게 입력해보세요.',
      '마이크 버튼을 눌러 음성으로 질문할 수도 있습니다.',
      '추천 질문을 클릭해도 답변을 받을 수 있습니다.',
      '답변이 부정확할 수 있으니, 중요한 내용은 담당부서에 꼭 확인하세요.',
      '단, 통합예약 관련 질문을 할 때는 날짜와 시설물(축구장, 야구장 등), 읍면동(나성동, 장군면 등)을 명확하게 입력해주세요.',
      '예시) 해밀동 배드민턴장 오늘 예약 가능한 시간이 있나요?'
    ],
    contact: '문의',
    phone: '대표전화: 044-120',
    close: '닫기'
  },
  en: {
    title: 'Chatbot User Guide',
    whatIs: 'What is AI Chungnyeong?',
    whatIsDesc: 'A service that provides answers using artificial intelligence trained on Sejong City related information.',
    howToUse: 'How to Use',
    howToUseItems: [
      'Freely type your questions in the input field.',
      'You can also ask questions by voice using the microphone button.',
      'You can get answers by clicking on recommended questions.',
      'Answers may be inaccurate, so please verify important information with the relevant department.',
      'However, when asking about integrated reservations, please clearly specify the date, facility (football field, baseball field, etc.), and administrative district (Naseong-dong, Janggun-myeon, etc.).',
      'Example) Are there any available reservation times for Hamil-dong badminton field today?'
    ],
    contact: 'Contact',
    phone: 'Main Phone: 044-120',
    close: 'Close'
  },
  ja: {
    title: 'チャットボット利用ガイド',
    whatIs: 'AI忠寧とは？',
    whatIsDesc: '世宗市関連情報を学習した人工知能が回答を提供するサービスです。',
    howToUse: '利用方法',
    howToUseItems: [
      '質問を入力欄に自由に入力してください。',
      'マイクボタンを押して音声で質問することもできます。',
      '推奨質問をクリックしても回答を受け取ることができます。',
      '回答が不正確な場合があるため、重要な内容は担当部署に必ず確認してください。',
      'ただし、統合予約に関する質問をする際は、日付と施設（サッカー場、野球場など）、邑面洞（羅城洞、将軍面など）を明確に入力してください。',
      '例）ハミルドンサッカー場で今日予約可能な時間はありますか？'
    ],
    contact: 'お問い合わせ',
    phone: '代表電話: 044-120',
    close: '閉じる'
  },
  cn: {
    title: '聊天机器人使用指南',
    whatIs: 'AI忠宁是什么？',
    whatIsDesc: '使用世宗市相关数据训练的人工智能，为您提供问答服务。',
    howToUse: '使用方法',
    howToUseItems: [
      '在输入框中自由输入您的问题。',
      '您也可以点击麦克风按钮进行语音提问。',
      '点击推荐问题也可以获得答案。',
      '答案可能不准确，重要内容请务必向相关部门确认。',
      '询问综合预约时，请写明日期、设施（足球场等）和区域（罗城洞等）。',
      '示例：海密洞羽毛球场今天可以预约吗？'
    ],
    contact: '联系方式',
    phone: '总机: 044-120',
    close: '关闭'
  }
};

interface SharedProps {
  question: string;
  setQuestion: React.Dispatch<React.SetStateAction<string>>;
  chatHistory: { question: string; answer?: string[]; info?: ResponseInfoType }[];
  handleSubmit: (e: React.FormEvent) => void;
  handleReset: () => void;
  isLoading: boolean;
  isStreaming: boolean;
  handleCardClick: (text: string) => void;
  stopFetching: () => void;
  linkify: (text: string) => string;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  autoResize: (isMobile: boolean) => void;
  isRounded: boolean;
  handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  scrollRef: React.RefObject<HTMLDivElement>;
  cardImages: string[];
  toggleRecording: () => void;
  isRecording: boolean;
  audioLevel: number;
  playTextToSpeech: (text: string) => void;
  stopTextToSpeech: () => void;
  isPlayingTTS: boolean;
}

interface DesktopLayoutProps extends SharedProps {
  isMobile: boolean;
  modalRef: React.RefObject<HTMLDivElement>;
  openIndex: number | null;
  setOpenIndex: (index: number | null) => void;
  hasStartedChat: boolean;
  scale: number;
  currentLanguage: any;
  handleLanguageChange: (lang: Language) => void;
  t: any;
  setGuideLanguage: React.Dispatch<React.SetStateAction<'ko' | 'en' | 'ja' | 'cn'>>;
  setShowGuideModal: React.Dispatch<React.SetStateAction<boolean>>;
}

interface MobileLayoutProps extends SharedProps {
  isMobile: boolean;
  currentLanguage: any;
  handleLanguageChange: (lang: Language) => void;
  t: any;
  setGuideLanguage: React.Dispatch<React.SetStateAction<'ko' | 'en' | 'ja' | 'cn'>>;
  setShowGuideModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const HomeContent = () => {
  const { currentLanguage, setLanguage, t } = useLanguage();
  const isMobile = useIsMobile();
  const history = useHistory();
  const location = useLocation();

  const modalRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const [hasStartedChat, setHasStartedChat] = useState(false);
  const [question, setQuestion] = useState('');
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const abortController = useRef<AbortController | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [isRounded, setIsRounded] = useState(true);
  const hasCompletedRef = useRef(false);
  const hasReceivedData = useRef(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for conversation tracking
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [chatHistory, setChatHistory] = useState<{ question: string; answer?: string[]; info?: ResponseInfoType }[]>([]);
  const [showIntroAlert, setShowIntroAlert] = useState(true);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [guideLanguage, setGuideLanguage] = useState<'ko' | 'en' | 'ja' | 'cn'>(currentLanguage);

  useEffect(() => {
    setGuideLanguage(currentLanguage);
  }, [currentLanguage]);

  useEffect(() => {
    scheduleIdleTask(() => {
      ensureSessionId().catch(() => {
        // 세션 초기화 실패 시 첫 API 호출에서 재시도
      });
    });
  }, []);

  // 오늘 다시보지 않기 체크박스 상태 추가
  const [dontShowToday, setDontShowToday] = useState(false);

  // STT API 음성 녹음 관련 상태
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- setAudioChunks used in recorder
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const [silenceTimeout, setSilenceTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [maxRecordingTimeout, setMaxRecordingTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [isPlayingTTS, setIsPlayingTTS] = useState(false);
  // 음성 녹음 후 자동 TTS 재생을 위한 플래그
  const [shouldAutoPlayTTS, setShouldAutoPlayTTS] = useState(false);

  useEffect(() => {
    return subscribeSessionExpired(() => {
      setShowSessionExpiredModal(true);
      setIsLoading(false);
      setIsStreaming(false);
      setIsPlayingTTS(false);
      abortController.current?.abort();
      setChatHistory((prev) => {
        const last = prev[prev.length - 1];
        if (last?.answer?.[0] === '__loading__') {
          return prev.slice(0, -1);
        }
        return prev;
      });
    });
  }, []);

  // 음성 녹음 후 자동 TTS 재생 로직
  useEffect(() => {
    // isStreaming이 false이고, isLoading도 false인 상태 (완전히 완료된 상태)에서만 실행
    if (!isStreaming && !isLoading && shouldAutoPlayTTS && chatHistory.length > 0) {
      const lastEntry = chatHistory[chatHistory.length - 1];
      if (lastEntry?.answer && lastEntry.answer.length > 0) {
        // 모든 답변 청크를 합쳐서 완전한 텍스트 생성
        const fullResponseText = lastEntry.answer
          .filter(chunk => chunk !== '__loading__')
          .join('');
        
        if (fullResponseText.trim() && fullResponseText.length > 10) {
          // 텍스트가 너무 길면 앞부분만 사용 (TTS 제한 고려)
          let textForTTS = fullResponseText;
          if (fullResponseText.length > 4000) {
            // 2000자를 넘으면 적절한 문장 끝에서 자르기
            const truncatedText = fullResponseText.substring(0, 4000);
            
            // 문장 구분자로 분할 (한국어, 영어, 일본어, 중국어 문장 끝 표시)
            const sentenceEndings = /[.!?。！？\n\n]/;
            const sentences = truncatedText.split(sentenceEndings);
            
            if (sentences.length > 1) {
              // 마지막 불완전한 문장 제거하고 나머지 문장들 사용
              sentences.pop();
              textForTTS = sentences.join('.') + '.';
            } else {
              // 문장 구분자가 없으면 단락 구분자로 시도
              const paragraphs = truncatedText.split(/\n\n/);
              if (paragraphs.length > 1) {
                textForTTS = paragraphs[0];
              } else {
                // 단락 구분자도 없으면 단순히 2000자로 자르기
                textForTTS = truncatedText;
              }
            }
          }
          
          // 최종 텍스트가 너무 짧으면 재생하지 않음
          if (textForTTS.trim().length < 5) {
            setShouldAutoPlayTTS(false);
            return;
          }
          
          // 더 충분한 딜레이를 주어 스트리밍이 완전히 끝나고 UI가 안정화된 후 재생
          setTimeout(() => {
            playTextToSpeech(textForTTS);
          }, 2000); // 1500ms → 2000ms로 증가
          
          setShouldAutoPlayTTS(false); // 플래그 리셋
        } else {
          setShouldAutoPlayTTS(false);
        }
      } else {
        setShouldAutoPlayTTS(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- playTextToSpeech intentionally excluded to avoid re-running on ref change
  }, [isStreaming, isLoading, shouldAutoPlayTTS, chatHistory]);

  // 팝업 표시 여부 확인 함수
  const shouldShowIntroAlert = () => {
    const today = new Date().toDateString();
    const lastDismissed = localStorage.getItem('introAlertDismissed');
    
    if (lastDismissed === today) {
      return false;
    }
    return true;
  };

  // 팝업 닫기 처리 함수
  const handleCloseIntroAlert = () => {
    if (dontShowToday) {
      const today = new Date().toDateString();
      localStorage.setItem('introAlertDismissed', today);
    }
    setShowIntroAlert(false);
  };

  useEffect(() => {
    // 페이지 로드 시 팝업 표시 여부 확인
    setShowIntroAlert(shouldShowIntroAlert());
    
    // 개발 모드에서 핫 리로드 시 대화 기록 초기화 (선택적)
    const nodeEnv = (globalThis as { process?: { env?: { NODE_ENV?: string } } }).process?.env
      ?.NODE_ENV;
    if (nodeEnv === 'development') {
      setChatHistory([]);
      setHasStartedChat(false);
      setQuestion('');
    }
  }, []);



  // 음성 레벨 분석 함수
  const analyzeAudioLevel = (analyserNode?: AnalyserNode) => {
    const currentAnalyser = analyserNode || analyser;
    if (!currentAnalyser) {
      return;
    }
    
    const dataArray = new Uint8Array(currentAnalyser.frequencyBinCount);
    currentAnalyser.getByteFrequencyData(dataArray);
    
    // 평균 음성 레벨 계산
    const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
    setAudioLevel(average);
    
    // 5초마다 현재 음성 레벨 로그 (너무 많은 로그 방지)
    if (Math.random() < 0.01) { // 약 1% 확률로 로그
    }
    
    // 무음 감지 (임계값: 8로 높임, 말하는 중 끊김 방지)
    if (average < 3) {
      // 이미 무음 타이머가 있으면 초기화하지 않음
      if (!silenceTimeout) {
        const timeout = setTimeout(() => {
          if (mediaRecorder && mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
          }
          if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
          }
          if (audioContext) {
            audioContext.close();
          }
          setIsRecording(false);
          setSilenceTimeout(null);
          if (maxRecordingTimeout) {
            clearTimeout(maxRecordingTimeout);
            setMaxRecordingTimeout(null);
          }
        }, 10000); // 15초 무음이면 자동 중지 (어르신 사용자 고려, 말하는 중 끊김 방지)

        setSilenceTimeout(timeout);
      }
    } else {
      // 음성이 감지되면 무음 타이머 취소
      if (silenceTimeout) {
        clearTimeout(silenceTimeout);
        setSilenceTimeout(null);
      }
    }
    
    // 계속 분석
    if (isRecording) {
      requestAnimationFrame(() => analyzeAudioLevel(currentAnalyser));
    } else {
    }
  };

  // STT API 음성 녹음 시작/중지 함수
  const toggleRecording = async () => {
    if (isRecording) {
      // 수동 녹음 중지
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioContext) {
        audioContext.close();
      }
      if (silenceTimeout) {
        clearTimeout(silenceTimeout);
        setSilenceTimeout(null);
      }
      if (maxRecordingTimeout) {
        clearTimeout(maxRecordingTimeout);
        setMaxRecordingTimeout(null);
      }
      setIsRecording(false);
    } else {
      // 녹음 시작
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 16000
          }
        });
        mediaStreamRef.current = stream;

        // Web Audio API 설정 (음성 레벨 분석용)
        const context = new AudioContext();
        const source = context.createMediaStreamSource(stream);
        const analyserNode = context.createAnalyser();
        
        analyserNode.fftSize = 256;
        analyserNode.smoothingTimeConstant = 0.8;
        source.connect(analyserNode);
        
        setAudioContext(context);
        setAnalyser(analyserNode);

        // 브라우저별 MIME 타입 지원 확인
        let mimeType = 'audio/webm';
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          mimeType = 'audio/webm;codecs=opus';
        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
          mimeType = 'audio/ogg;codecs=opus';
        } else if (MediaRecorder.isTypeSupported('audio/wav')) {
          mimeType = 'audio/wav';
        }

        const recorder = new MediaRecorder(stream, { mimeType });
        const chunks: Blob[] = [];

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunks.push(event.data);
          }
        };

        recorder.onstop = async () => {
          const audioBlob = new Blob(chunks, { type: mimeType });
          
          // AudioContext 정리
          if (context.state !== 'closed') {
            context.close();
          }
          setAudioContext(null);
          setAnalyser(null);
          setAudioLevel(0);
          
          // 무음 타이머 정리
          if (silenceTimeout) {
            clearTimeout(silenceTimeout);
            setSilenceTimeout(null);
          }
          
          await sendAudioToSTT(audioBlob);
          setAudioChunks([]);
        };

        recorder.onerror = (event) => {
          setIsRecording(false);
        };

        setMediaRecorder(recorder);
        setAudioChunks(chunks);
        recorder.start(1000); // 1초마다 청크 생성 (메모리 효율성 및 안정성 향상)
        setIsRecording(true);

        // 최대 녹음 시간 제한 설정 (5분 = 300,000ms)
        const maxTimeout = setTimeout(() => {
          if (mediaRecorder && mediaRecorder.state === 'recording') {
            mediaRecorder.stop();
          }
          if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
          }
          if (audioContext) {
            audioContext.close();
          }
          setIsRecording(false);
          setMaxRecordingTimeout(null);
          alert('녹음 시간이 5분을 초과하여 자동으로 중지되었습니다.');
        }, 180000); // 3분

        setMaxRecordingTimeout(maxTimeout);

        // 음성 레벨 분석 시작 (analyserNode를 직접 전달)
        requestAnimationFrame(() => analyzeAudioLevel(analyserNode));

      } catch (error) {
        if ((error as Error).name === 'NotAllowedError') {
          alert('마이크 접근 권한이 거부되었습니다. 브라우저 설정에서 마이크 권한을 허용해주세요.');
        } else if ((error as Error).name === 'NotFoundError') {
          alert('마이크를 찾을 수 없습니다. 마이크가 연결되어 있는지 확인해주세요.');
        } else if ((error as Error).name === 'NotSupportedError') {
          alert('이 브라우저에서는 음성 녹음이 지원되지 않습니다.');
        } else {
          alert('마이크 접근 중 오류가 발생했습니다: ' + (error as Error).message);
        }
      }
    }
  };

  // TTS 음성 중지 함수
  const stopTextToSpeech = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
      setIsPlayingTTS(false);
    }
  };

  // TTS API로 텍스트를 음성으로 변환하고 재생하는 함수
  const playTextToSpeech = async (text: string) => {
    try {
      // 이미 재생 중인 음성이 있다면 중지
      if (isPlayingTTS && currentAudio) {
        stopTextToSpeech();
        await new Promise(resolve => setTimeout(resolve, 200)); // 잠시 대기
      }

      setIsPlayingTTS(true);

      const formData = new FormData();
      formData.append('text', text);
      formData.append('speaker', 'nhajun'); // 기본 화자 (백엔드에서 언어에 맞게 자동 선택)
      formData.append('volume', '0');
      formData.append('speed', '0');
      formData.append('pitch', '0');
      formData.append('emotion', '0');
      formData.append('format', 'mp3');
      formData.append('language', currentLanguage); // 현재 선택된 언어 추가
      formData.append('sessionID', await ensureSessionId());

      const response = await fetch(`${API_BASE_URL}/api/tts`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        const audioBlob = await response.blob();
        
        // Blob이 유효한지 확인
        if (audioBlob.size === 0) {
          setIsPlayingTTS(false);
          alert('음성 데이터가 비어있습니다.');
          return;
        }
        
        // 오디오 URL 생성
        const audioUrl = URL.createObjectURL(audioBlob);
        
        // 오디오 재생
        const audio = new Audio(audioUrl);
        setCurrentAudio(audio);
        
        // 이벤트 리스너 설정
        audio.onloadstart = () => {
        };
        
        audio.oncanplaythrough = () => {
        };
        
        audio.onplay = () => {
          setIsPlayingTTS(true);
        };
        
        audio.onpause = () => {
          setIsPlayingTTS(false);
        };
        
        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          setCurrentAudio(null);
          setIsPlayingTTS(false);
        };
        
        audio.onerror = () => {
          URL.revokeObjectURL(audioUrl);
          setCurrentAudio(null);
          setIsPlayingTTS(false);
          alert('음성 재생에 실패했습니다.');
        };
        
        // 메타데이터 로딩 완료 후 재생하도록 수정
        audio.onloadedmetadata = async () => {
          try {
            await audio.play();
          } catch (error) {
            setIsPlayingTTS(false);
            alert('음성 재생에 실패했습니다: ' + (error as Error).message);
          }
        };
        
        // 오디오 로드 시작
        audio.load();
        
      } else if (await isSessionExpiredResponse(response)) {
        setIsPlayingTTS(false);
        triggerSessionExpired();
      } else {
        setIsPlayingTTS(false);
        alert(`음성 변환에 실패했습니다. (${response.status}: ${response.statusText})`);
      }
    } catch (error) {
      setIsPlayingTTS(false);
      if (error instanceof Error && error.message.includes('타임아웃')) {
        alert('음성 로딩 시간이 초과되었습니다.');
      } else {
        alert('음성 변환 중 오류가 발생했습니다.');
      }
    }
  };

  // STT API로 음성 전송 함수
  const sendAudioToSTT = async (audioBlob: Blob) => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('audio', audioBlob, 'audio.webm');
      formData.append('language', currentLanguage); // 현재 선택된 언어 추가
      formData.append('sessionID', await ensureSessionId());

      const response = await fetch(`${API_BASE_URL}/api/stt`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        const contentType = response.headers.get('content-type');

        if (contentType && contentType.includes('application/json')) {
          const result = await response.json();

          if (result.text && result.text.trim()) {
            const recognizedText = result.text.trim();

            // STT 결과로 자동 검색 실행 (입력창에 표시하지 않고 바로 전송)
            // 음성 녹음으로부터 온 질문임을 표시하여 자동 TTS 재생되도록 함
            handleFetch(recognizedText, false, true);
            
            // 입력창은 비워둠 (자동 전송되므로)
            setQuestion('');
          } else {
            alert('음성 인식 결과가 비어있습니다.');
          }
        } else {
          // JSON이 아닌 응답 (HTML 등)
          await response.text();
          alert('STT API에서 HTML 페이지를 받았습니다. 백엔드 라우터 등록을 확인해주세요.');
        }
      } else if (await isSessionExpiredResponse(response)) {
        setIsLoading(false);
        triggerSessionExpired();
      } else {
        await response.text();

        if (response.status === 404) {
          alert('STT API 엔드포인트를 찾을 수 없습니다. 백엔드에서 라우터가 등록되었는지 확인해주세요.');
        } else {
          alert(`음성 인식에 실패했습니다. (${response.status}: ${response.statusText})`);
        }
      }
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        alert('STT API 서버에 연결할 수 없습니다. 백엔드 서버가 실행 중인지 확인해주세요.');
      } else if (error instanceof SyntaxError && error.message.includes('JSON')) {
        alert('STT API가 HTML 페이지를 반환했습니다. 백엔드 설정을 확인해주세요.');
      } else {
        alert(`음성 전송 중 오류가 발생했습니다: ${error}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  //Expands text input field when new lines are added
  const autoResize = (isMobile: boolean) => {
    if (inputRef.current) {
      const textarea = inputRef.current;

      window.requestAnimationFrame(() => {
        if (!inputRef.current) return;

        // Always reset first
        textarea.style.height = 'auto';

        const computed = getComputedStyle(textarea);
        const lineHeight = parseFloat(computed.lineHeight || '22');
        const paddingTop = parseFloat(computed.paddingTop || '0');
        const paddingBottom = parseFloat(computed.paddingBottom || '0');

        const initialHeight = lineHeight + paddingTop + paddingBottom;
        const maxHeight = (lineHeight * 4) + paddingTop + paddingBottom;

        const desiredHeight = Math.min(Math.max(textarea.scrollHeight, initialHeight), maxHeight);
        textarea.style.height = `${desiredHeight}px`;

        const isMultiline = desiredHeight > initialHeight + 1;
        setIsRounded(!isMultiline);
      });
    }
  };

  useEffect(() => {
    if (isMobile) return;
    if (!modalRef.current) return;

    const observer = new ResizeObserver(entries => {
      const height = entries[0].contentRect.height;

      // Example logic: scale down if modal height < 500px
      if (height < 510) {
        setScale(Math.max(height / 500, 0.7));  // Don't shrink below 70%
      } else {
        setScale(1);
      }
    });

    observer.observe(modalRef.current);

    return () => observer.disconnect();
  }, [isMobile]);

  // Focuses cursor on input field on load
  useEffect(() => {
    if (!isMobile) {
      inputRef.current?.focus();
    }
  }, [isMobile]);

  // Automatic Scroll to Newest Message
  useEffect(() => {
    if (chatHistory.length > 0) {
      // 스크롤을 맨 아래로 이동시키되, 부드러운 애니메이션 없이 즉시 이동
      scrollRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
  }, [chatHistory]);

  const resetConversationState = async () => {
    if (abortController.current) {
      abortController.current.abort();
    }
    setIsLoading(false);
    setIsStreaming(false);
    setChatHistory([]);
    setHasStartedChat(false);
    setQuestion('');
    setConversationId(undefined);
    await refreshSessionId();
  };

  const handleReset = async () => {
    await resetConversationState();
  };

  const handleLanguageChange = (lang: Language) => {
    if (lang === currentLanguage) {
      return;
    }
    // setLanguage()가 location.href로 전체 페이지를 다시 열므로,
    // refreshSessionId()는 불필요하고 ensureSessionId()만 1회 호출되게 한다.
    if (abortController.current) {
      abortController.current.abort();
    }
    setLanguage(lang);
  };

  const stopFetching = async () => {
    // HTTP 요청 취소
    if (abortController.current) {
      abortController.current.abort();
    }

    // 백엔드 취소 API 호출
    try {
      await ensureSessionId();
      const sessionId = getSessionId();
      await fetch(`${API_BASE_URL}/cancel/${sessionId}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch {
    }

    // 취소 메시지 추가
    setChatHistory((prev) => {
      const last = prev[prev.length - 1];
      if (!last) return prev;

      // 마지막 항목이 로딩 중이거나 빈 응답인 경우 취소 메시지로 교체
      if (!last.answer || last.answer.length === 0 || last.answer[0] === '__loading__') {
        return [
          ...prev.slice(0, -1),
          {
            ...last,
            answer: ['응답이 중단되었습니다.']
          }
        ];
      }

      // 이미 응답이 있는 경우 취소 메시지 추가
      return [
        ...prev.slice(0, -1),
        {
          ...last,
          answer: [...last.answer, '응답이 중단되었습니다.']
        }
      ];
    });

    setIsLoading(false);
    setIsStreaming(false);
  };

  const cardImages = [
    publicAsset('/assets/img/prompt_box_01_251022.webp'),
    publicAsset('/assets/img/prompt_box_07_260129.webp'),
    publicAsset('/assets/img/prompt_box_02_251022.webp'),
    publicAsset('/assets/img/prompt_box_04.webp'),
    publicAsset('/assets/img/prompt_box_09_260914.webp'),
    publicAsset('/assets/img/prompt_box_08_260601.webp'),
  ];

  const handleFetch = (trimmedQuestion: string, fromCategory?: boolean, fromVoice?: boolean) => {
    if (isLoading) return;
    
    // 이전 요청 취소 (메시지 추가 없이)
    if (abortController.current) {
      abortController.current.abort();
    }
    abortController.current = new AbortController();
    
    // 로딩 상태만 정리
    setIsLoading(false);
    setIsStreaming(false);
    
    // 음성 녹음으로부터 온 질문인 경우에만 자동 TTS 플래그 설정
    if (fromVoice) {
      setShouldAutoPlayTTS(true);
    }

    setHasStartedChat(true);
    setChatHistory((prev) => [
      ...prev,
      { question: trimmedQuestion, answer: ['__loading__'] },
    ]);
    setIsLoading(true);
    setIsStreaming(true);
    hasCompletedRef.current = false;

    fetchSSE(
      { question: trimmedQuestion, fromCategory, language: currentLanguage },
      (messages, infoData, incomingId) => {
        setChatHistory((prev) => {
          const last = prev[prev.length - 1];
          if (!last) return prev;

          const newChunk = messages
            .map((msg) => (typeof msg === 'string' ? msg : msg?.answer?.join('') || ''))
            .join('');

          const prevAnswer = last.answer?.[0] ?? '';
          const nextAnswer =
            prevAnswer === '__loading__'
              ? newChunk || '__loading__'
              : prevAnswer + newChunk;

          return [
            ...prev.slice(0, -1),
            {
              ...last,
              answer: [nextAnswer],
              // 토큰 청크의 빈 info로 rich_sources가 덮이지 않도록 병합
              info:
                infoData &&
                (infoData.rich_sources?.length ||
                  infoData.library_after_books ||
                  infoData.library_ui_mode ||
                  infoData.contact?.department ||
                  infoData.contact?.phone ||
                  infoData.ui)
                  ? { ...last.info, ...infoData }
                  : last.info || infoData,
              conversationId: incomingId,
            },
          ];
        });

        if (messages.length > 0) hasReceivedData.current = true;
        if (messages.length === 0 && hasReceivedData.current) {
          setIsLoading(false);
          setIsStreaming(false);
          hasReceivedData.current = false;
        }
      },
      abortController.current
    );
  };

  const handleCardClick = (text: string) => {
    if (isLoading) return;

    const trimmed = text.trim();
    if (!trimmed) return;

    // setQuestion(trimmed);
    handleFetch(trimmed, true, false); // fromCategory=true, fromVoice=false
    setQuestion('');
  };

  // 공지 등에서 사이드바 FAQ 클릭 → 홈 이동 후 바로 채팅
  useEffect(() => {
    const state = location.state as { pendingChatQuestion?: string } | null;
    let pending = state?.pendingChatQuestion?.trim() || '';
    if (!pending) {
      try {
        pending = sessionStorage.getItem('pendingChatQuestion')?.trim() || '';
      } catch {
        /* ignore */
      }
    }
    if (!pending) return;

    const id = window.setTimeout(() => {
      try {
        sessionStorage.removeItem('pendingChatQuestion');
      } catch {
        /* ignore */
      }
      if (state?.pendingChatQuestion) {
        history.replace({ pathname: location.pathname, state: {} });
      }
      handleCardClick(pending);
    }, 50);

    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 진입 시 1회
  }, [location.key]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    const trimmed = question.trim();
    if (!trimmed) return;

    handleFetch(trimmed, false, false); // fromCategory=false, fromVoice=false

    setTimeout(() => {
      setQuestion('');
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
      }
    }, 0);
  };

  // Shared Props b/w Mobile and Desktop
  const sharedProps = {
    question,
    setQuestion,
    chatHistory,
    handleSubmit,
    handleReset,
    isLoading,
    isStreaming,
    handleCardClick,
    stopFetching,
    linkify,
    inputRef,
    autoResize,
    isRounded,
    handleKeyDown,
    scrollRef,
    cardImages,
    toggleRecording,
    isRecording,
    audioLevel,
    playTextToSpeech,
    stopTextToSpeech,
    isPlayingTTS
  };

  return (
    <>
      {showIntroAlert && (
        <HoverMessageOverlay onClick={() => setShowIntroAlert(false)}>
          {isMobile ? (
            <MobileHoverMessage $lang={currentLanguage} onClick={(e) => e.stopPropagation()}>
              <IntroTitle $lang={currentLanguage}>{t.intro.title}</IntroTitle>
              <Divider />
              <MessageContent $lang={currentLanguage}>
                <div>{t.intro.description}</div>
                <br></br>
                <div>{t.intro.disclaimer}</div>
              </MessageContent>
              <DontShowTodayCheckbox>
                <input
                  type="checkbox"
                  id="dontShowToday"
                  checked={dontShowToday}
                  onChange={(e) => setDontShowToday(e.target.checked)}
                />
                <label htmlFor="dontShowToday">{t.intro.dontShowToday}</label>
              </DontShowTodayCheckbox>
              <ConfirmButton onClick={handleCloseIntroAlert}>{t.common.confirm}</ConfirmButton>
            </MobileHoverMessage>
          ) : (
            <DesktopIntroModal $lang={currentLanguage} onClick={(e) => e.stopPropagation()}>
              <IntroTitle $lang={currentLanguage}>{t.intro.title}</IntroTitle>
              <Divider />
              <IntroContentRow $lang={currentLanguage}>
                <IntroImage src={publicAsset('/assets/img/sejong_icon.webp')} alt="AI충녕 이미지" loading="lazy" />
                <MessageTextBlock $lang={currentLanguage}>
                  <div>{t.intro.description}</div>
                  <div>{t.intro.disclaimer}</div>
                </MessageTextBlock>
              </IntroContentRow>
              <DontShowTodayCheckbox>
                <input
                  type="checkbox"
                  id="dontShowTodayDesktop"
                  checked={dontShowToday}
                  onChange={(e) => setDontShowToday(e.target.checked)}
                />
                <label htmlFor="dontShowTodayDesktop">{t.intro.dontShowToday}</label>
              </DontShowTodayCheckbox>
              <ConfirmButton onClick={handleCloseIntroAlert}>{t.common.confirm}</ConfirmButton>
            </DesktopIntroModal>
          )}
        </HoverMessageOverlay >
      )}

      {/* Render both Desktop and Mobile but check isMobile to choose which to show */}
      {/* Both DesktopLayout and MobileLayout will return null depending on isMobile's status */}
      {/* When conditionally rendering inside the return statement, React will re-render DOMs*/}
      {/* Causes a bug where the user types one character and the input field is defocused */}

      <DesktopLayout
        isMobile={isMobile}
        modalRef={modalRef}
        openIndex={openIndex}
        setOpenIndex={setOpenIndex}
        scale={scale}
        hasStartedChat={hasStartedChat}
        currentLanguage={currentLanguage}
        handleLanguageChange={handleLanguageChange}
        t={t}
        setGuideLanguage={setGuideLanguage}
        setShowGuideModal={setShowGuideModal}
        {...sharedProps}
      />

      <MobileLayout
        {...sharedProps}
        currentLanguage={currentLanguage}
        handleLanguageChange={handleLanguageChange}
        t={t}
        isMobile={isMobile}
        setGuideLanguage={setGuideLanguage}
        setShowGuideModal={setShowGuideModal}
      />

      {/* 물음표 버튼은 이제 HeaderActions로 이동됨 */}


      <SessionExpiredModal open={showSessionExpiredModal} />

      {/* 가이드 모달 */}
      {showGuideModal && (
        <GuideModalOverlay onClick={() => setShowGuideModal(false)}>
          <GuideModal $lang={guideLanguage} onClick={e => e.stopPropagation()}>
            <GuideHeader>
              <GuideTitle $lang={guideLanguage}>{guideTexts[guideLanguage].title}</GuideTitle>
              <LanguageSelector>
                <LanguageButton 
                  $isActive={guideLanguage === 'ko'} 
                  onClick={() => setGuideLanguage('ko')}
                >
                  한국어
                </LanguageButton>
                <LanguageButton 
                  $isActive={guideLanguage === 'en'} 
                  onClick={() => setGuideLanguage('en')}
                >
                  English
                </LanguageButton>
                <LanguageButton 
                  $isActive={guideLanguage === 'ja'} 
                  onClick={() => setGuideLanguage('ja')}
                >
                  日本語
                </LanguageButton>
                <LanguageButton 
                  $isActive={guideLanguage === 'cn'} 
                  onClick={() => setGuideLanguage('cn')}
                >
                  中文
                </LanguageButton>
              </LanguageSelector>
            </GuideHeader>
            <GuideContent $lang={guideLanguage}>
              <div>
                <strong>{guideTexts[guideLanguage].whatIs}</strong><br />
                {guideTexts[guideLanguage].whatIsDesc}<br /><br />
                <strong>{guideTexts[guideLanguage].howToUse}</strong><br />
                {guideTexts[guideLanguage].howToUseItems.map((item, index) => (
                  <span key={index}>
                    - {item}<br />
                  </span>
                ))}
                <br />
                <strong>{guideTexts[guideLanguage].contact}</strong><br />
                {guideTexts[guideLanguage].phone}
              </div>
            </GuideContent>
            <GuideCloseButton onClick={() => setShowGuideModal(false)}>
              {guideTexts[guideLanguage].close}
            </GuideCloseButton>
          </GuideModal>
        </GuideModalOverlay>
      )}

      
    </>
  );
};

// Desktop Component
// Most modular components have been extracted into the /components folder
const DesktopLayout: React.FC<DesktopLayoutProps> = ({
  isMobile,
  modalRef,
  openIndex,
  setOpenIndex,
  hasStartedChat,
  cardImages,
  scale,
  currentLanguage,
  handleLanguageChange,
  t,
  setGuideLanguage,
  setShowGuideModal,
  ...sharedProps
}) => {
  if (isMobile) return null;

  return (
    <NewWrapper>
      <Sidebar onItemClick={sharedProps.handleCardClick} isMobile={false} />
      <DesktopMainContent>
        <NoticeBanner />
        <ChatHeader>
          <HeaderActions>
            <RequestButtonWrapper
              href="https://www.sejong.go.kr/prog/bbs/646/eeungDB/sub03_01/write.do"
              target="_blank"
              rel="noopener noreferrer"
            >
              <StyledMuiButton
                variant="text"
                startIcon={<SearchIcon />}
              >
                {t.common.requestData}
              </StyledMuiButton>
            </RequestButtonWrapper>
          </HeaderActions>

          {/* 오른쪽 버튼 그룹 - 언어선택과 다크모드 토글, 물음표 버튼 */}
          <RightButtonGroup>
            <LanguageSelectorComponent
              currentLanguage={currentLanguage}
              onLanguageChange={handleLanguageChange}
            />

            {/* 다크모드 토글 버튼 */}
            <DarkModeToggle />

            {/* 물음표 버튼 */}
            <QuestionHeaderButton
              type="button"
              onClick={() => {
                setGuideLanguage(currentLanguage);
                setShowGuideModal(true);
              }}
              aria-label={t.common.userGuideTitle}
            >
              <QuestionIcon aria-hidden />
            </QuestionHeaderButton>
          </RightButtonGroup>
        </ChatHeader>

        {hasStartedChat ? (
          <DesktopChatWrapper>
            <ChatArea>
              <ChatWindow
                {...sharedProps}
                handleKeyDown={sharedProps.handleKeyDown}
                scrollRef={sharedProps.scrollRef}
                isMobile={false}
              />
            </ChatArea>
            <DesktopFixedChatInput>
              <ChatInput
                {...sharedProps}
                handleKeyDown={sharedProps.handleKeyDown}
                showResetButton={false}
                isMobile={false}
                toggleRecording={sharedProps.toggleRecording}
                isRecording={sharedProps.isRecording}
              />
            </DesktopFixedChatInput>
          </DesktopChatWrapper>
        ) : (
          <WelcomeSection>
            <WelcomeTagline>{t.common.welcomeTagline}</WelcomeTagline>
            <WelcomeTitle>{t.common.welcomeTitle}</WelcomeTitle>
            <DesktopInputSection>
              <ChatInput
                {...sharedProps}
                handleKeyDown={sharedProps.handleKeyDown}
                showResetButton={false}
                isMobile={false}
                toggleRecording={sharedProps.toggleRecording}
                isRecording={sharedProps.isRecording}
              />
            </DesktopInputSection>
            <QuickQuestions>
              {t.predefinedQuestions.slice(0, 6).map((question: string, index: number) => {
                const isPermitCard = isPermitShortcutCard(question);
                const isAskCard = isLifeInconvenienceAskCard(question);
                const isShortcutCard = isPermitCard;
                // 생활불편: 호버 안내 있어도 클릭 시 menuItems처럼 바로 질문 전송
                const isHoverOnly = !isShortcutCard && !isAskCard && !!t.hoverMessages[index];

                return (
                  <QuickQuestionCard
                    key={index}
                    onClick={
                      isShortcutCard
                        ? () => {
                          openPermitPage();
                        }
                        : isAskCard
                          ? () => sharedProps.handleCardClick(question)
                        : isHoverOnly
                          ? undefined
                          : () => sharedProps.handleCardClick(question)
                    }
                    style={{
                      backgroundImage: `url(${cardImages[index]})`,
                      cursor: isShortcutCard || isAskCard || !isHoverOnly ? 'pointer' : 'help'
                    }}
                    data-tooltip={t.hoverMessages[index] || null}
                    className={
                      (isHoverOnly || isAskCard || (isShortcutCard && t.hoverMessages[index]))
                        ? 'hover-only'
                        : ''
                    }
                  >
                    {question}
                    {(isHoverOnly || isAskCard || isShortcutCard) && t.hoverMessages[index] && (
                      <HoverIndicator>?</HoverIndicator>
                    )}
                  </QuickQuestionCard>
                );
              })}
            </QuickQuestions>
          </WelcomeSection>
        )}
      </DesktopMainContent>
    </NewWrapper>
  );
};

// Mobile Component
// No modular components have been extracted yet
const MobileLayout: React.FC<MobileLayoutProps> = ({
  isMobile,
  currentLanguage,
  handleLanguageChange,
  t,
  setGuideLanguage,
  setShowGuideModal,
  ...sharedProps
}) => {
  const {
    chatHistory,
    handleReset,
    handleCardClick,
    cardImages
  } = sharedProps;

  const { isDarkMode } = useDarkMode();
  const history = useHistory();
  const { hasNotices } = usePublicNotices();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const bottomSheetRef = useRef<HTMLDivElement>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [openSection, setOpenSection] = useState<number | null>(null);
  const [showPredefined, setShowPredefined] = useState(false);
  const [isPredefinedOpen, setIsPredefinedOpen] = useState(false);
  const [hoverMessageModal, setHoverMessageModal] = useState<{
    text: string;
    permit?: boolean;
  } | null>(null);
  const [isLanguagePanelOpen, setIsLanguagePanelOpen] = useState(false);

  const toggleSection = (index: number) => {
    setOpenSection(prev => (prev === index ? null : index));
  };

  const openMenu = () => {
    setShowMenu(true);
    setTimeout(() => setIsMenuOpen(true), 0);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setTimeout(() => {
      setShowMenu(false);
      setOpenSection(null);
    }, 300);
  };

  const openPredefined = () => {
    setShowPredefined(true);
    setTimeout(() => setIsPredefinedOpen(true), 0);
  };

  const closePredefined = () => {
    setIsPredefinedOpen(false);
    setTimeout(() => setShowPredefined(false), 300);
  };

  useEffect(() => {
    let rafId = 0;
    const setViewportHeight = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      });
    };

    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', setViewportHeight);
    };
  }, []);


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isBottomSheetOpen &&
        bottomSheetRef.current &&
        !bottomSheetRef.current.contains(event.target as Node)
      ) {
        setIsBottomSheetOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isBottomSheetOpen]);

  if (!isMobile) return null;

  return (
      <MobileWrapper>
        {/* Header */}
        <MobileHeader>
          <IconButton type="button" onClick={openMenu} aria-label={t.common.openMenu}>
            <MenuIcon aria-hidden />
          </IconButton>

          <BrandGroup onClick={handleReset} style={{ cursor: 'pointer' }}>
            <BrandText>AI충녕</BrandText>
            <BrandIcon
              src={publicAsset('/assets/img/character_mobile_1.webp')}
              alt="Character Icon"
              {...({ fetchpriority: 'high' } as React.ImgHTMLAttributes<HTMLImageElement>)}
            />
          </BrandGroup>

          <RightButtonsGroup>
            <DarkModeToggle />
            <LanguageIconButton
              type="button"
              onClick={() => setIsLanguagePanelOpen(!isLanguagePanelOpen)}
              aria-label={t.common.selectLanguage}
              aria-expanded={isLanguagePanelOpen}
            >
              <GlobeIcon isDarkMode={isDarkMode} />
            </LanguageIconButton>
          </RightButtonsGroup>
        </MobileHeader>

        {/* 모바일 언어 선택 패널 */}
        {isLanguagePanelOpen && (
          <>
            {/* 배경 오버레이 - 바깥 클릭으로 닫기 */}
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(2px)',
                zIndex: 1999
              }}
              onClick={() => setIsLanguagePanelOpen(false)}
            />
            <MobileLanguagePanel onClick={(e) => e.stopPropagation()}>
            <LanguageGrid>
              {LANGUAGES.map((lang) => (
                <LanguageCard
                  key={lang.code}
                  onClick={() => {
                    handleLanguageChange(lang.code);
                    setIsLanguagePanelOpen(false);
                  }}
                  $isSelected={lang.code === currentLanguage}
                  $isMobile={true}
                >
                  <CardFlag>{lang.flag}</CardFlag>
                  <CardName>{lang.name}</CardName>
                  {lang.code === currentLanguage && (
                    <SelectedIndicator>
                      <svg width="16" height="12" viewBox="0 0 16 12" fill="none">
                        <path d="M1 6L6 11L15 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </SelectedIndicator>
                  )}
                </LanguageCard>
              ))}
            </LanguageGrid>
            </MobileLanguagePanel>
          </>
        )}

        {chatHistory.length > 0 ? (
          <MobileChatWrapper>
            <ChatWindow
              {...sharedProps}
              showInput={false}   // hide ChatInput inside ChatWindow
              isMobile={true}
            />
            {/* Fix ChatInput at the bottom after chat starts */}
            <FixedChatInput>
              <ChatInput
                {...sharedProps}
                isMobile={true}
                isChatActive={true}
                showResetButton={false}
                onOpenPredefined={openPredefined}
              />
            </FixedChatInput>
          </MobileChatWrapper>
        ) : (
          <>
            <NoticeBanner />
            <StatusBar isMobile />

            <MainContent>
              <MobileTagline>{t.common.welcomeTagline}</MobileTagline>
              <MobileTitle>{t.common.welcomeTitle}</MobileTitle>
              <ChatInput
                {...sharedProps}
                isMobile={true}
                isChatActive={false}
                showResetButton={false}
                onOpenPredefined={undefined}
              />
              <MobileCardGrid>
                {t.predefinedQuestions.map((text: string, i: number) => (
                  <SquareCard
                    key={i}
                    onClick={() => {
                      const isPermitCard =
                        i === PERMIT_PREDEFINED_QUESTION_INDEX || isPermitShortcutCard(text);
                      if (isPermitCard) {
                        const msg = t.hoverMessages[i];
                        if (msg) {
                          setHoverMessageModal({ text: msg, permit: true });
                        } else {
                          openPermitPage();
                        }
                        return;
                      }
                      // 생활불편 신고: 사이드바 menuItems처럼 바로 질문 전송
                      if (isLifeInconvenienceAskCard(text)) {
                        handleCardClick(text);
                        return;
                      }
                      if (t.hoverMessages[i]) {
                        setHoverMessageModal({ text: t.hoverMessages[i] || t.common.noHoverMessage });
                      } else {
                        handleCardClick(text);
                      }
                    }}
                  >
                    <CardBackground
                      src={cardImages[i]}
                      alt=""
                      aria-hidden
                      loading={i < 3 ? 'eager' : 'lazy'}
                      {...({
                        fetchpriority: i === 0 ? 'high' : 'low',
                      } as React.ImgHTMLAttributes<HTMLImageElement>)}
                      decoding="async"
                    />
                    <CardLabel>{text}</CardLabel>
                  </SquareCard>
                ))}
              </MobileCardGrid>
            </MainContent>
          </>
        )}

        {/* Helper Components */}
        <>
          {/* Bottom Sheet */}
          {showPredefined && (
            <PredefinedOverlay isOpen={isPredefinedOpen} onClick={closePredefined}>
              <PredefinedContainer isOpen={isPredefinedOpen} onClick={(e) => e.stopPropagation()}>
                <ClosePredefined onClick={closePredefined}>
                  <CloseIcon />
                </ClosePredefined>
                {t.predefinedQuestions.map((questionText: string, index: number) => (
                  <PredefinedItem
                    key={index}
                    onClick={() => {
                      const isPermitCard =
                        index === PERMIT_PREDEFINED_QUESTION_INDEX ||
                        isPermitShortcutCard(questionText);
                      if (isPermitCard) {
                        const msg = t.hoverMessages[index];
                        closePredefined();
                        if (msg) {
                          setHoverMessageModal({ text: msg, permit: true });
                        } else {
                          openPermitPage();
                        }
                        return;
                      }
                      if (isLifeInconvenienceAskCard(questionText)) {
                        handleCardClick(questionText);
                        closePredefined();
                        return;
                      }
                      if (t.hoverMessages[index]) {
                        setHoverMessageModal({ text: t.hoverMessages[index] || t.common.noHoverMessage });
                      } else {
                        handleCardClick(questionText);
                      }
                      closePredefined();
                    }}
                  >
                    <IconCircle src={cardImages[index]} alt="icon" />
                    <span>{questionText}</span>
                  </PredefinedItem>
                ))}
              </PredefinedContainer>
            </PredefinedOverlay>
          )}

          {/* Hamburger Menu */}
          {showMenu && (
            <MenuBackdrop isOpen={isMenuOpen} onClick={closeMenu}>
              <HamburgerMenuOverlay isOpen={isMenuOpen} onClick={(e) => e.stopPropagation()}>
                <CloseButton type="button" onClick={closeMenu} aria-label={t.common.closeMenu}>
                  <CloseIcon aria-hidden />
                </CloseButton>

                <MenuContent>
                  {t.menuItems
                    .filter((menu: any) => !(isNoticeMenuLabel(menu.label) && !hasNotices))
                    .map((menu: any, index: number) => {
                    // 챗봇 도움말 메뉴인지 확인
                    const isChatbotHelp = menu.label === '챗봇 도움말' ||
                                         menu.label === 'Chatbot Help' ||
                                         menu.label === 'チャットボットヘルプ' ||
                                         menu.label === '聊天机器人帮助';
                    const isNotice = isNoticeMenuLabel(menu.label);

                    return (
                    <AccordionSection key={index}>
                      <AccordionHeader
                        onClick={() => {
                          if (isChatbotHelp) {
                            // 챗봇 도움말이면 바로 모달 열기
                            closeMenu();
                            setTimeout(() => {
                              setGuideLanguage(currentLanguage);
                              setShowGuideModal(true);
                            }, 300);
                          } else if (isNotice) {
                            closeMenu();
                            history.push('/notices');
                          } else {
                            // 일반 메뉴는 섹션 토글
                            toggleSection(index);
                          }
                        }}
                      >
                        <span>{menu.label}</span>
                        {!isChatbotHelp && !isNotice && (
                          <ChevronIconWrapper isOpen={openSection === index}>
                            <ChevronIcon />
                          </ChevronIconWrapper>
                        )}
                      </AccordionHeader>

                    {!isNotice && (
                    <AccordionContent isOpen={openSection === index}>
                      {menu.items.map((item: string, idx: number) => {
                        const contactPhoneItems = [
                          '담당자 전화번호',
                          'Contact Phone Number',
                          '担当者電話番号',
                          '负责人电话号码',
                        ];
                        const isLinkItem = contactPhoneItems.includes(item);
                        const linkUrl = 'https://www.sejong.go.kr/prog/deptPerson/kor/sub01_0103/deptList.do';

                        const isRelatedSiteItem =
                          isRelatedSiteMenuLabel(menu.label) && isRelatedSiteLink(item);

                        // 챗봇 가이드 항목 체크 (다국어)
                        const isGuideItem = item === '챗봇 이용 가이드' ||
                                          item === 'Chatbot User Guide' ||
                                          item === 'チャットボット利用ガイド' ||
                                          item === '聊天机器人使用指南';

                        return isLinkItem ? (
                          <a
                            key={idx}
                            href={linkUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none', color: 'inherit' }}
                            onClick={closeMenu}
                          >
                            <AccordionItem>{item}</AccordionItem>
                          </a>
                        ) : isRelatedSiteItem ? (
                          <a
                            key={idx}
                            href={getRelatedSiteUrl(item)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none', color: 'inherit' }}
                            onClick={closeMenu}
                          >
                            <AccordionItem>{item}</AccordionItem>
                          </a>
                        ) : isGuideItem ? (
                          <AccordionItem
                            key={idx}
                            onClick={() => {
                              // 먼저 사이드바 닫기
                              closeMenu();
                              // 약간의 딜레이 후 모달 열기 (사이드바 애니메이션 완료 대기)
                              setTimeout(() => {
                                setGuideLanguage(currentLanguage);
                                setShowGuideModal(true);
                              }, 300);
                            }}
                          >
                            {item}
                          </AccordionItem>
                        ) : (
                          <AccordionItem
                            key={idx}
                            onClick={() => {
                              handleCardClick(item);
                              closeMenu();
                            }}
                          >
                            {item}
                          </AccordionItem>
                        );
                      })}

                    </AccordionContent>
                    )}
                  </AccordionSection>
                  );
                })}

                <MobileRequestButtonWrapper
                  href="https://www.sejong.go.kr/prog/bbs/646/eeungDB/sub03_01/write.do"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMenu}
                >
                  <MobileRequestButton
                    variant="text"
                    startIcon={<SearchIcon />}
                  >
                    {t.common.requestData}
                  </MobileRequestButton>
                </MobileRequestButtonWrapper>

                  <MenuFooter>
                    {t.sidebar.mainPhone}<br />
                    {t.sidebar.weekdayHours}<br />
                    {t.sidebar.weekendHours}<br />
                    {t.sidebar.afterHours}
                  </MenuFooter>
                </MenuContent>

              </HamburgerMenuOverlay>
            </MenuBackdrop>
          )}

          {/* Hover Message Modal */}
          {hoverMessageModal && (
            <HoverMessageOverlay onClick={() => setHoverMessageModal(null)}>
              <MobileHoverMessage onClick={(e) => e.stopPropagation()}>
                <MessageContent>
                  {hoverMessageModal.text.split('\n').map((line, idx) => {
                    const isSecondary = isHoverMessageSecondaryLine(line);
                    return (
                      <div key={idx}>
                        {isSecondary ? line : <strong>{line}</strong>}
                      </div>
                    );
                  })}
                </MessageContent>
                <HoverModalActions>
                  {hoverMessageModal.permit && (
                    <ShortcutNavButton
                      type="button"
                      onClick={() => {
                        setHoverMessageModal(null);
                        openPermitPage();
                      }}
                    >
                      {t.common.openPermitPage}
                    </ShortcutNavButton>
                  )}
                  <ConfirmButton type="button" onClick={() => setHoverMessageModal(null)}>
                    {t.common.confirm}
                  </ConfirmButton>
                </HoverModalActions>
              </MobileHoverMessage>
            </HoverMessageOverlay>
          )}
        </>

      </MobileWrapper>
  );
};

// Home 컴포넌트
const Home = () => {
  return <HomeContent />;
};

export default Home;

// #region ===== Desktop Styling =====

// Desktop Layout Wrapper
const NewWrapper = styled.div`
  display: flex;
  height: 100vh;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  background: ${props => props.theme.bgColor};
  color: ${props => props.theme.textDefaultColor};
`;

const DesktopMainContent = styled.div`
  flex: 1;
  min-width: 0; /* 공지 마퀴 등 넓은 자식이 메인 폭을 밀지 못하게 */
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.bodyBgColor};
  border-radius: 12px;
  border: 1px solid ${props => props.theme.borderGrayColor};
  overflow: hidden;
  min-height: 0; /* Important for flex child */
`;
const ChatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 3rem 1.6rem;
  background: transparent;
`;


// Material-UI Button styled components
const StyledMuiButton = styled(Button)`
  && {
    position: relative;
    overflow: hidden;
    color: ${props => props.theme.textDefaultColor === '#FFF' ? '#FFF' : 'rgb(23, 47, 92)'} !important;
    font-size: 1.7rem !important;
    font-weight: 600 !important;
    padding: 0.6rem 1.2rem !important;
    text-transform: none !important;
    border-radius: 12px !important;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
      transition: left 0.5s;
      z-index: 1;
    }

    &:hover {
      background-color: ${props => props.theme.questionBoxBg} !important;
      color: ${props => props.theme.logoColor} !important;
      transform: translateY(-3px) scale(1.02);
      box-shadow: 0 6px 20px rgba(139, 92, 246, 0.3);
    }

    &:active {
      transform: translateY(0);
    }

    &:hover::before {
      left: 100%;
    }

    & .MuiButton-startIcon {
      transition: transform 0.3s ease;
    }

    &:hover .MuiButton-startIcon {
      transform: scale(1.1);
    }
  }
`;

const RequestButtonWrapper = styled.a`
  display: inline-block;
  text-decoration: none;
`;

const MobileRequestButton = styled(Button)`
  && {
    margin-top: 2rem !important;
    color: ${props => props.theme.textDefaultColor} !important;
    font-size: 1.6rem !important;
    font-weight: 700 !important;
    padding: 1rem 1.5rem !important;
    text-transform: none !important;
    width: 100% !important;
    border-radius: 12px !important;

    .MuiButton-startIcon {
      transition: transform 0.3s ease;
    }

    &:hover {
      background-color: ${props => props.theme.questionBoxBg} !important;
      color: ${props => props.theme.logoColor} !important;
      transform: translateY(-2px);
    }

    &:active {
      transform: translateY(0);
    }

    &:hover .MuiButton-startIcon {
      transform: scale(1.1);
    }
  }
`;

const MobileRequestButtonWrapper = styled.a`
  display: block;
  text-decoration: none;
  width: 100%;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const RightButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;


const ChatArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const WelcomeSection = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 4rem 2rem;
  gap: 3rem;

  @media (max-width: 1024px) {
    padding: 3rem 1.5rem;
    gap: 2rem;
  }
`;

const WelcomeTagline = styled.span`
  font-size: 1.6rem;
  font-weight: 600;
  background: linear-gradient(135deg, #8b9dc9 0%, #9b8bb0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 0.35rem 0;
  text-align: center;
  letter-spacing: -0.02em;
  line-height: 1.4;
  display: block;
  white-space: pre-line;

  @media (max-width: 1024px) {
    font-size: 1.35rem;
  }
`;

const WelcomeTitle = styled.h2`
  font-size: 2.8rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  text-align: center;
  letter-spacing: -0.02em;

  @media (max-width: 1024px) {
    font-size: 2.4rem;
  }
`;

const QuickQuestions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  max-width: 800px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    max-width: 500px;
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    /* 768px 태블릿에서 카드 가로 길이 늘리기 */
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    max-width: 700px;
  }
`;

const QuickQuestionCard = styled.div`
  padding: 2rem 1.5rem;
  background-size: cover;
  background-position: center bottom;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  font-size: 1.5rem;
  color: white;
  font-weight: 600;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  position: relative;
  white-space: pre-line;

  @media (max-width: 768px) {
    min-height: 90px;
    font-size: 1.2rem;
    padding: 1.2rem 1rem;
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    /* 768px 태블릿에서 카드 크기 키우기 */
    min-height: 140px;
    font-size: 1.6rem;
    padding: 2.2rem 1.8rem;
  }

  &:hover {
    transform: translateY(-6px) scale(1.02);
    box-shadow: 0 12px 35px rgba(139, 92, 246, 0.3);
    filter: brightness(1.08);
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }

  &.hover-only {
    /* 상단 행 카드들 (첫 3개) - 말풍선이 위로 */
    &:nth-child(-n+3) {
      &:hover {
        z-index: 10000;
      }
      
      &:hover::after {
        content: attr(data-tooltip);
        position: absolute;
        top: -20px;
        left: 0;
        width: 100%;
        background: rgba(0, 0, 0, 0.85);
        color: white;
        padding: 1rem;
        border-radius: 8px;
        font-size: 1.2rem;
        font-weight: 400;
        line-height: 1.5;
        text-shadow: none;
        z-index: 10001;
        text-align: left;
        white-space: pre-wrap;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
        transform: translateY(-100%);
      }
      
      &:hover::before {
        content: '';
        position: absolute;
        top: -10px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        border-top: 10px solid rgba(0, 0, 0, 0.85);
        z-index: 10001;
      }
    }
    
    /* 하단 행 카드들 (나머지) - 말풍선이 아래로 */
    &:nth-child(n+4) {
      &:hover {
        z-index: 10000;
      }
      
      &:hover::after {
        content: attr(data-tooltip);
        position: absolute;
        bottom: -20px;
        left: 0;
        width: 100%;
        background: rgba(0, 0, 0, 0.85);
        color: white;
        padding: 1rem;
        border-radius: 8px;
        font-size: 1.2rem;
        font-weight: 400;
        line-height: 1.5;
        text-shadow: none;
        z-index: 10001;
        text-align: left;
        white-space: pre-wrap;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
        transform: translateY(100%);
      }
      
      &:hover::before {
        content: '';
        position: absolute;
        bottom: -10px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        border-bottom: 10px solid rgba(0, 0, 0, 0.85);
        z-index: 10001;
      }
    }
  }
`;

const HoverIndicator = styled.span`
  position: absolute;
  bottom: 10px;
  right: 10px;
  width: 22px;
  height: 22px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: bold;
`;


const DesktopChatWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const DesktopFixedChatInput = styled.div`
  position: sticky;
  bottom: 0;
  width: 100%;
  background: ${props => props.theme.bodyBgColor};
  z-index: 200;
  box-sizing: border-box;
  padding: 1.5rem 3rem;
  border-top: none;

  @media (max-width: 1024px) {
    padding: 1rem 2rem;
  }
`;

const DesktopInputSection = styled.div`
  padding: 1rem 0 2rem 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
`;


//#endregion

//#region ====== Mobile Styling =====

const MobileWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: calc(var(--vh, 1vh) * 100);
  width: 100%;
  overflow: hidden;
  position: relative;

  background-color: ${props => props.theme.bgColor === '#1a1a1a' ? '#2a2a2a' : '#F0F2F5'};
  color: ${props => props.theme.textDefaultColor};
  max-width: 100vw;
  min-width: 280px;
  box-sizing: border-box;

  /* 320px 화면에서 최적화 */
  @media (max-width: 320px) {
    min-width: 280px;
  }
`;

const IconButton = styled.button`
  background: ${props => props.theme.bgColor === '#1a1a1a' ? '#2a2a2a' : '#F0F2F5'};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.iconColor};
  transition: all 0.2s ease;
  border-radius: 8px;
  padding: 4px;
  width: 40px;
  height: 40px;

  &:hover {
    background: ${props => props.theme.bgColor === '#1a1a1a' ? '#333333' : '#e8ecef'};
    transform: scale(1.05);
  }

  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
    stroke: currentColor;
  }
`;

const LanguageIconButton = styled.button`
  background: ${props => props.theme.bgColor === '#1a1a1a' ? '#2a2a2a' : '#F0F2F5'};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.textDefaultColor};
  transition: all 0.2s ease;
  border-radius: 8px;
  padding: 4px;
  width: 40px;
  height: 40px;
  margin-left: 6px;

  &:hover {
    background: ${props => props.theme.bgColor === '#1a1a1a' ? '#333333' : '#e8ecef'};
    transform: scale(1.05);
  }

  svg {
    width: 20px;
    height: 20px;
    stroke: currentColor;
  }

  svg path,
  svg circle {
    stroke: currentColor;
  }
`;

const RightButtonsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;


const MobileLanguagePanel = styled.div`
  position: fixed;
  top: 6rem;
  right: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  border: none;
  z-index: 2000;
  min-width: 200px;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const BrandText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.8rem;
  color: ${props => props.theme.textDefaultColor};
  line-height: 1;
`;

const BrandGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
`;

const BrandIcon = styled.img`
  height: 26px;
  width: 20px;
  display: block;
`;


// 체크박스 스타일 컴포넌트 추가
const DontShowTodayCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  font-size: 1.2rem;
  color: #666;
  
  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #22468A;
  }
  
  label {
    cursor: pointer;
    user-select: none;
    font-weight: 500;
    
    &:hover {
      color: #22468A;
    }
  }
`;



const MobileHeader = styled.div`
  position: sticky;
  top: 0;
  width: 100%;
  height: 6rem;
  z-index: 100;
  border-bottom: 1px solid ${props => props.theme.bgColor === '#1a1a1a' ? '#404040' : '#cccccc'};
  background: ${props => props.theme.bgColor === '#1a1a1a' ? '#2a2a2a' : '#F0F2F5'};

  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1.5rem;

  ${BrandGroup} {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const MobileTagline = styled.span`
  position: absolute;
  top: 12%;
  left: 0;
  right: 0;
  width: 100%;
  transform: translateY(-50%);
  font-size: clamp(1.25rem, 3.5vw, 1.5rem);
  font-weight: 600;
  background: linear-gradient(135deg, #8b9dc9 0%, #9b8bb0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  text-align: center;
  padding: 0 1rem;
  line-height: 1.4;
  letter-spacing: -0.02em;
  display: block;
  white-space: pre-line;
  pointer-events: none;
  box-sizing: border-box;

  /* 매우 작은 화면(SE 등): 세종날씨 바로 아래 쪽으로 */
  @media (max-height: 740px) {
    top: 8%;
  }

  @media (max-width: 320px) {
    font-size: clamp(1.1rem, 3vw, 1.35rem);
  }
`;

const MobileTitle = styled.h2`
  font-size: clamp(2.2rem, 5vw, 2.75rem);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-align: center;
  margin: 0;
  padding: 0 1rem;
  line-height: 1.3;

  /* 작은 화면에서 더 작게 */
  @media (max-width: 320px) {
    font-size: clamp(1.8rem, 4.5vw, 2.2rem);
    padding: 0 0.5rem;
  }
`;

const ChatInputContainer = styled.div<{ $isRounded: boolean }>`
  display: flex;
  align-items: flex-end;
  background: transparent;
  border-radius: ${({ $isRounded }) => ($isRounded ? '25px' : '10px')};
  padding: 0.5rem 0.5rem 0.5rem 1rem;
  border: 1px solid #ccc;
  flex: 1;
  transition: border-radius 0.2s ease;
`;

const HamburgerMenuOverlay = styled.div<{ isOpen: boolean }>`
  width: 80%;
  max-width: 300px;
  height: 100%;
  background: ${props => props.theme.bgColor};
  box-shadow: 2px 0 5px rgba(0,0,0,0.3);
  padding: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  transform: ${({ isOpen }) => isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  transition: transform 0.3s ease;
`;

const CloseButton = styled.button`
  position: sticky;
  top: 1rem;
  right: 1rem;
  margin: 1rem 1rem 0 auto;
  background: none;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  z-index: 10;
  display: block;
  width: fit-content;
  color: ${props => props.theme.textDefaultColor};

  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
  }
`;

const MenuContent = styled.div`
  padding: 0 1.5rem 1.5rem;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;

  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.questionBoxBg};
    border-radius: 3px;
    margin: 0.5rem 0;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.textThinColor};
    border-radius: 3px;

    &:hover {
      background: ${props => props.theme.textDefaultColor};
    }
  }

  /* Firefox 스크롤바 스타일링 */
  scrollbar-width: thin;
  scrollbar-color: ${props => props.theme.textThinColor} ${props => props.theme.questionBoxBg};
`;

const MenuBackdrop = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, ${({ isOpen }) => isOpen ? 0.3 : 0});  // Dynamic opacity
  z-index: 1000;

  display: flex;
  align-items: stretch;

  transition: background 0.3s ease;
  pointer-events: ${({ isOpen }) => isOpen ? 'auto' : 'none'};   // Prevent clicks when invisible
`;

const AccordionSection = styled.div`
  border-bottom: 1px solid #eee;
`;

const AccordionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 0;
  cursor: pointer;
  font-size: 1.5rem;
  font-weight: bold;
`;

const ChevronIconWrapper = styled.span<{ isOpen: boolean }>`
  display: inline-block;
  transition: transform 0.3s ease;
  transform: rotate(${props => (props.isOpen ? '180deg' : '0deg')});
  transform-origin: center;

  svg {
    width: 1rem;
    height: 1rem;
    stroke: ${props => props.theme.textDefaultColor};
    stroke-width: 2;
  }
`;

const AccordionContent = styled.div<{ isOpen: boolean }>`
  background: ${props => props.theme.questionBoxBg};
  padding: ${({ isOpen }) => (isOpen ? '0.5rem 1rem' : '0 1rem')};
  max-height: ${({ isOpen }) => (isOpen ? '500px' : '0')};  /* Adjust 500px if needed */
  overflow: hidden;
  transition: max-height 0.3s ease, padding 0.3s ease;
`;

const AccordionItem = styled.div`
  padding: 0.75rem 0;
  font-size: 1.25rem;
  cursor: pointer;
  font-weight: 400;
  color: ${props => props.theme.textDefaultColor};

  &:hover {
    color: ${props => props.theme.logoColor};
    font-weight: 500;
  }
`;

const PredefinedOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(8px);

  opacity: ${props => props.isOpen ? 1 : 0};
  transition: opacity 0.3s ease;

  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 2000;

  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
`;

const PredefinedContainer = styled.div<{ isOpen: boolean }>`
  background: white;
  width: 100%;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  border: none;
  padding: 2rem 1.5rem 3rem 1.5rem;
  box-shadow: 0 -2px 10px rgba(0,0,0,0.1);

  transform: translateY(${props => props.isOpen ? '0' : '100%'});
  transition: transform 0.3s ease;
`;

const ClosePredefined = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;

  svg {
    width: 24px;
    height: 24px;
    cursor: pointer;
  }
`;

const PredefinedItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 0;
  cursor: pointer;
  border-bottom: 1px solid #eee;

  span {
    margin-left: 1rem;
    font-size: 1.5rem;
    font-weight: bold;
    color: #333;
    white-space: pre-line;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const IconCircle = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid #ddd;
`;

const MobileChatWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 0 0.75rem; /* 좌우 패딩 추가 */

  ${ChatInputContainer} {
    width: 100%;
    padding: 1rem;
  }
`;

const fadeIn = keyframes`
  to {
    opacity: 1;
  }
`;

const slideFadeDown = keyframes`
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const HoverMessageOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
  padding: 1rem;
  box-sizing: border-box;

  opacity: 0;
  animation: ${fadeIn} 0.3s forwards;

  /* 모바일에서 패딩 조정 */
  @media (max-width: 480px) {
    padding: 0.5rem;
  }
`;

const MobileHoverMessage = styled.div<{ $lang?: string }>`
  background: white;
  padding: ${(props) => (props.$lang === 'cn' ? '1.75rem 1.5rem' : '2rem')};
  border-radius: 12px;
  border: none;
  width: ${(props) =>
    props.$lang === 'cn' ? 'min(380px, calc(100vw - 2rem))' : '320px'};
  max-width: ${(props) =>
    props.$lang === 'cn' ? 'min(380px, calc(100vw - 2rem))' : '320px'};
  text-align: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  font-family: ${(props) =>
    props.$lang === 'cn'
      ? '"Noto Sans SC", "PingFang SC", "Microsoft YaHei", sans-serif'
      : "'Noto Sans KR', sans-serif"};
  box-sizing: border-box;
  overflow-x: hidden;

  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1rem;

  transform: translateY(-20px);
  opacity: 0;
  animation: ${slideFadeDown} 0.3s forwards;

  @media (max-width: 480px) {
    width: calc(100vw - 2rem);
    max-width: calc(100vw - 2rem);
    padding: ${(props) => (props.$lang === 'cn' ? '1.5rem 1.25rem' : '1.5rem')};
  }

  @media (max-width: 360px) {
    width: calc(100vw - 1.5rem);
    max-width: calc(100vw - 1.5rem);
    padding: 1rem;
  }
`;

const MessageContent = styled.div<{ $lang?: string }>`
  margin-bottom: 1.5rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  text-align: left;

  div {
    margin: 0.5rem 0;
    font-size: ${(props) => (props.$lang === 'cn' ? '1.25rem' : '1.4rem')};
    color: #333;
    white-space: normal;
    word-break: ${(props) => (props.$lang === 'cn' ? 'break-all' : 'break-word')};
    overflow-wrap: anywhere;
    line-height: 1.65;
    font-weight: 400;
    max-width: 100%;
    min-width: 0;
  }

  strong {
    font-weight: 700;
  }
`;

const ConfirmButton = styled.button`
  padding: 0.8rem 2rem;
  background-color: #22468A;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.4rem;
  cursor: pointer;

  &:hover {
    background-color: #1a3570;
  }
`;

const HoverModalActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  max-width: 100%;
`;

const ShortcutNavButton = styled.button`
  padding: 0.8rem 2rem;
  background-color: #15803d;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.4rem;
  cursor: pointer;
  font-family: 'Noto Sans KR', sans-serif;

  &:hover {
    background-color: #166534;
  }
`;

const MenuFooter = styled.div`
  margin-top: 2rem;
  font-size: 1.2rem;
  font-weight: 500;
  color: #666;
  line-height: 1.6;
  text-align: left;
  
  font-family: 'Noto Sans KR', sans-serif;
`;

const MainContent = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 0.25rem 1rem;
  gap: 1.5rem;

  /* 320px 화면에서 padding 조정 */
  @media (max-width: 320px) {
    padding: 0 0.15rem 1rem;
  }
  box-sizing: border-box;
  width: 100%;
`;

const MobileCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.8rem;
  width: 96%;
  max-width: 320px;
  padding-top: 1rem;

  /* 작은 화면에서 간격과 너비 조정 */
  @media (max-width: 360px) {
    gap: 0.6rem;
    width: 98%;
  }

  /* 320px 화면에서 최적화 */
  @media (max-width: 320px) {
    gap: 0.4rem;
    width: 96%;
  }

  /* 768px 태블릿에서 카드 가로 길이 늘리기 */
  @media (min-width: 769px) and (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    width: 90%;
    max-width: 650px;
  }
`;

const SquareCard = styled.div`
  aspect-ratio: 1 / 1;

  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.3rem;

  color: white;
  font-weight: 600;
  font-size: clamp(1.0rem, 2.2vw, 1.3rem);
  text-align: center;
  line-height: 1.1;
  white-space: pre-line;

  word-break: break-all;
  overflow-wrap: anywhere;
  hyphens: auto;

  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
  position: relative;
  overflow: hidden;

  transition: all 0.3s ease;

  /* 작은 화면에서 더 공격적인 텍스트 크기 조정 */
  @media (max-width: 360px) {
    font-size: clamp(1.0rem, 2vw, 1.1rem);
    padding: 0.25rem;
    line-height: 1.05;
  }
  
  /* 320px 이하에서 매우 작은 텍스트 */
  @media (max-width: 320px) {
    font-size: clamp(1.0rem, 1.8vw, 1.0rem);
    padding: 0.2rem;
    line-height: 1.0;
  }
  
  /* 매우 작은 화면에서 극도로 작게 */
  @media (max-width: 280px) {
    font-size: clamp(0.65rem, 1.6vw, 0.9rem);
    padding: 0.15rem;
    line-height: 0.95;
  }

  /* 320px 화면에서 padding 최적화 */
  @media (max-width: 320px) {
    padding: 0.15rem;
  }

  /* 768px 태블릿에서 카드 크기 키우기 */
  @media (min-width: 769px) and (max-width: 1024px) {
    padding: 0.5rem;
    font-size: clamp(1.1rem, 2.5vw, 1.5rem);
    min-height: 120px;
  }

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 12px rgba(0,0,0,0.2);
    filter: brightness(1.05);
  }
`;

const CardBackground = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  z-index: 0;
  pointer-events: none;
`;

const CardLabel = styled.span`
  position: relative;
  z-index: 1;
`;

//background: white;
const FixedChatInput = styled.div`
  position: sticky;
  bottom: 0;
  width: 100%;
  z-index: 200;
  box-sizing: border-box;
`;

const DesktopIntroModal = styled.div<{ $lang?: string }>`
  background: white;
  padding: ${(props) => (props.$lang === 'cn' ? '2rem 2.25rem' : '2.5rem 3.25rem')};
  border-radius: 1rem;
  border: none;
  width: ${(props) =>
    props.$lang === 'cn' ? 'min(560px, calc(100vw - 2.4rem))' : '500px'};
  max-width: ${(props) =>
    props.$lang === 'cn' ? 'min(560px, calc(100vw - 2.4rem))' : '500px'};
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  font-family: ${(props) =>
    props.$lang === 'cn'
      ? '"Noto Sans SC", "PingFang SC", "Microsoft YaHei", sans-serif'
      : "'Noto Sans KR', sans-serif"};
  box-sizing: border-box;
  overflow-x: hidden;

  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${(props) => (props.$lang === 'cn' ? '1.5rem' : '2rem')};

  transform: translateY(-2rem);
  opacity: 0;
  animation: ${slideFadeDown} 0.3s forwards;
`;

const IntroTitle = styled.h2<{ $lang?: string }>`
  font-size: ${(props) => (props.$lang === 'cn' ? '1.75rem' : '2rem')};
  font-weight: bold;
  color: #22468A;
  margin: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: ${(props) => (props.$lang === 'cn' ? 'break-all' : 'keep-all')};
  line-height: 1.35;
`;

const Divider = styled.hr`
  width: 100%;
  border: none;
  border-top: 1px solid #ddd;
  margin: 0;
`;

const IntroContentRow = styled.div<{ $lang?: string }>`
  display: flex;
  flex-direction: ${(props) => (props.$lang === 'cn' ? 'column' : 'row')};
  align-items: ${(props) => (props.$lang === 'cn' ? 'center' : 'center')};
  justify-content: center;
  gap: ${(props) => (props.$lang === 'cn' ? '1.25rem' : '3rem')};
  width: 100%;
  min-width: 0;
  text-align: ${(props) => (props.$lang === 'cn' ? 'center' : 'left')};
`;

const IntroImage = styled.img`
  width: 12rem;
  height: auto;
  flex-shrink: 0;
`;

const MessageTextBlock = styled.div<{ $lang?: string }>`
  flex: 1;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-sizing: border-box;

  div {
    font-size: ${(props) => (props.$lang === 'cn' ? '1.25rem' : '1.4rem')};
    color: #333;
    white-space: normal;
    word-break: ${(props) => (props.$lang === 'cn' ? 'break-all' : 'keep-all')};
    overflow-wrap: anywhere;
    line-height: 1.65;
    font-weight: 400;
    max-width: 100%;
    min-width: 0;
  }
`;



//#endregion

const QuestionHeaderButton = styled.button`
  position: relative;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  padding: 4px;
  color: white;
  border-radius: 50%;
  box-shadow: 0 3px 10px rgba(102, 126, 234, 0.3);

  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
    transition: all 0.3s ease;
  }

  &:hover {
    background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
    transform: translateY(-2px) scale(1.08);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  }

  &:active {
    transform: translateY(0) scale(1.02);
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  }
`;


const GuideModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.2rem;
  box-sizing: border-box;
  opacity: 0;
  animation: ${fadeIn} 0.3s forwards;
`;

const GuideModal = styled.div<{ $lang?: string }>`
  background: #fff;
  border-radius: 16px;
  font-family: ${(props) =>
    props.$lang === 'cn'
      ? '"Noto Sans SC", "PingFang SC", "Microsoft YaHei", sans-serif'
      : 'inherit'};
  padding: ${(props) => (props.$lang === 'cn' ? '2rem 1.75rem' : '2.5rem')};
  width: ${(props) =>
    props.$lang === 'cn' ? 'min(540px, calc(100vw - 2.4rem))' : 'min(450px, calc(100vw - 2.4rem))'};
  max-width: ${(props) => (props.$lang === 'cn' ? '540px' : '450px')};
  max-height: min(85vh, calc(100vh - 2.4rem));
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  box-sizing: border-box;
  transform: translateY(-20px);
  opacity: 0;
  animation: ${slideFadeDown} 0.3s forwards;
  
  @media (max-width: 480px) {
    padding: ${(props) => (props.$lang === 'cn' ? '1.5rem 1.25rem' : '1.8rem')};
    width: calc(100vw - 2.4rem);
    max-width: calc(100vw - 2.4rem);
    border-radius: 12px;
  }
  
  @media (max-width: 360px) {
    padding: ${(props) => (props.$lang === 'cn' ? '1.25rem 1rem' : '1.5rem')};
    width: calc(100vw - 1.6rem);
    max-width: calc(100vw - 1.6rem);
    max-height: min(90vh, calc(100vh - 1.6rem));
  }
`;

const GuideHeader = styled.div`
  width: 100%;
  min-width: 0;
  margin-bottom: 2rem;
  overflow: hidden;
  
  /* 모바일에서 간격 조정 */
  @media (max-width: 480px) {
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 320px) {
    margin-bottom: 1.2rem;
  }
`;

const GuideTitle = styled.h2<{ $lang?: string }>`
  font-size: ${(props) => (props.$lang === 'cn' ? '1.75rem' : '2rem')};
  color: #22468A;
  margin-bottom: 1.5rem;
  font-weight: bold;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  text-align: center;
  position: relative;
  overflow-wrap: anywhere;
  word-break: ${(props) => (props.$lang === 'cn' ? 'break-all' : 'break-word')};
  line-height: 1.35;
  
  @media (max-width: 480px) {
    font-size: ${(props) => (props.$lang === 'cn' ? '1.55rem' : '1.8rem')};
    margin-bottom: 1.2rem;
  }
  
  @media (max-width: 320px) {
    font-size: ${(props) => (props.$lang === 'cn' ? '1.4rem' : '1.6rem')};
    margin-bottom: 1rem;
  }
`;

const LanguageSelector = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  width: 100%;
  max-width: 100%;
  min-width: 0;
`;

const LanguageButton = styled.button<{ $isActive: boolean }>`
  padding: 0.5rem 1rem;
  border: none;
  background: ${props => props.$isActive ? props.theme.btnColor : props.theme.bodyBgColor};
  color: ${props => props.$isActive ? props.theme.whiteText : props.theme.textDefaultColor};
  border-radius: 20px;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: ${props => props.$isActive ? '600' : '400'};

  &:hover {
    background: ${props => props.$isActive ? '#1a3570' : '#f8f9fa'};
    transform: translateY(-1px);
  }
  
  /* 작은 화면에서 크기 조정 */
  @media (max-width: 480px) {
    padding: 0.4rem 0.8rem;
    font-size: 1.1rem;
  }
  
  @media (max-width: 320px) {
    padding: 0.3rem 0.6rem;
    font-size: 1rem;
    border-radius: 16px;
  }
`;

const GuideContent = styled.div<{ $lang?: string }>`
  font-size: ${(props) => (props.$lang === 'cn' ? '1.2rem' : '1.4rem')};
  color: #333;
  line-height: ${(props) => (props.$lang === 'cn' ? '1.65' : '1.7')};
  margin-bottom: 2rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  flex: 1 1 auto;
  box-sizing: border-box;
  overflow-wrap: anywhere;
  word-break: ${(props) => (props.$lang === 'cn' ? 'break-all' : 'break-word')};

  & > div {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: ${(props) => (props.$lang === 'cn' ? 'break-all' : 'break-word')};
  }

  span {
    display: block;
    max-width: 100%;
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: ${(props) => (props.$lang === 'cn' ? 'break-all' : 'break-word')};
    padding-left: 0.25rem;
    text-indent: -0.25rem;
  }
  
  strong {
    color: #22468A;
    display: block;
    margin-bottom: 0.5rem;
    font-size: ${(props) => (props.$lang === 'cn' ? '1.3rem' : '1.5rem')};
    max-width: 100%;
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: ${(props) => (props.$lang === 'cn' ? 'break-all' : 'break-word')};
  }
  
  br {
    content: "";
    display: block;
    margin: 0.8rem 0;
  }
  
  @media (max-width: 480px) {
    font-size: ${(props) => (props.$lang === 'cn' ? '1.15rem' : '1.3rem')};
    line-height: 1.6;
    margin-bottom: 1.5rem;
    
    strong {
      font-size: ${(props) => (props.$lang === 'cn' ? '1.25rem' : '1.4rem')};
      margin-bottom: 0.4rem;
    }
    
    br {
      margin: 0.6rem 0;
    }
  }
  
  @media (max-width: 320px) {
    font-size: ${(props) => (props.$lang === 'cn' ? '1.05rem' : '1.2rem')};
    line-height: 1.5;
    margin-bottom: 1.2rem;
    
    strong {
      font-size: ${(props) => (props.$lang === 'cn' ? '1.15rem' : '1.3rem')};
      margin-bottom: 0.3rem;
    }
    
    br {
      margin: 0.5rem 0;
    }
  }
`;

const GuideCloseButton = styled.button`
  align-self: center;
  max-width: 100%;
  box-sizing: border-box;
  padding: 0.8rem 3rem;
  background: #22468A;
  color: #fff;
  border: none;
  border-radius: 25px;
  font-size: 1.4rem;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #1a3570;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
  
  /* 모바일에서 크기 조정 */
  @media (max-width: 480px) {
    padding: 0.7rem 2.5rem;
    font-size: 1.3rem;
    border-radius: 20px;
  }
  
  @media (max-width: 320px) {
    padding: 0.6rem 2rem;
    font-size: 1.2rem;
    border-radius: 18px;
  }
`;




// eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for dev banner
const DevelopmentNotice = styled.div`
  background: linear-gradient(135deg, #ff6b6b, #ee5a24);
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  font-size: 1.6rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 1rem;
  box-shadow: 0 4px 15px rgba(238, 90, 36, 0.3);
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% {
      transform: scale(1);
      box-shadow: 0 4px 15px rgba(238, 90, 36, 0.3);
    }
    50% {
      transform: scale(1.02);
      box-shadow: 0 6px 20px rgba(238, 90, 36, 0.4);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 4px 15px rgba(238, 90, 36, 0.3);
    }
  }
`;

// #region ===== Language Selector Panel Styles =====

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for panel animation
const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for panel animation
const slideOutRight = keyframes`
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(100%);
  }
`;





const LanguageGrid = styled.div`
  padding: 1.5rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
`;

const LanguageCard = styled.button<{ $isSelected: boolean; $isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: ${props => props.$isMobile ? '0.875rem' : '1rem'};
  background: ${props => props.$isSelected ?
    'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)' :
    'white'
  };
  border: 2px solid ${props => props.$isSelected ? '#2196f3' : '#e1e8f0'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: ${props => props.$isMobile ? '0.9rem' : '1rem'};
  font-weight: ${props => props.$isSelected ? '600' : '500'};
  color: ${props => props.$isSelected ? '#1976d2' : '#33363f'};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    border-color: #2196f3;

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(0);
  }
`;

const CardFlag = styled.span`
  font-size: 1.5rem;
  line-height: 1;
  flex-shrink: 0;
`;

const CardName = styled.span`
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SelectedIndicator = styled.div`
  color: #2196f3;
  display: flex;
  align-items: center;
  flex-shrink: 0;

  svg {
    display: block;
  }
`;

// #endregion